import { AlertTriangle, CalendarClock, Mail, Server, ShieldCheck } from "lucide-react";
import { Badge, cn } from "@wa/ui";
import { daysUntil, yearsSince, EXPIRY_WARN_DAYS, type DomainCheck } from "../../../../lib/domainCheck";

export type T = (k: string, vars?: Record<string, string | number>) => string;

/**
 * Parsed view of one domain check - shared by the detail-page card and the
 * list-row modal.
 *
 * Order follows the sales conversation: who serves them now (the two provider
 * tiles), how established they are (registration date and age), then the raw
 * record lists for whoever wants to verify the conclusion.
 */
export function DomainSummary({ check, t }: { check: DomainCheck; t: T }) {
  const age = yearsSince(check.registeredAt);
  const left = daysUntil(check.expiresAt);
  const expiryTone = left == null ? null : left < 0 ? "bad" : left <= EXPIRY_WARN_DAYS ? "warn" : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <ProviderTile
          icon={Server}
          label={t("ariregister.dns.dnsProvider")}
          value={check.dnsProvider}
          fallback={check.nameservers[0] ?? t("ariregister.dns.unknownProvider")}
        />
        <ProviderTile
          icon={Mail}
          label={t("ariregister.dns.mailProvider")}
          value={check.mailProvider}
          fallback={check.mxRecords.length ? check.mxRecords[0]!.exchange : t("ariregister.dns.noMx")}
        />
      </div>

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label={t("ariregister.dns.registered")}>
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="size-3 text-muted-foreground" />
            {fmtDate(check.registeredAt)}
            {age != null && (
              <span className="text-xs text-muted-foreground">{t("ariregister.dns.yearsOld", { years: age })}</span>
            )}
          </span>
        </Field>

        <Field label={t("ariregister.dns.expires")}>
          <span
            className={cn(
              "inline-flex items-center gap-1.5",
              expiryTone === "bad" && "text-destructive",
              expiryTone === "warn" && "text-amber-600 dark:text-amber-400",
            )}
          >
            {expiryTone && <AlertTriangle className="size-3" />}
            {fmtDate(check.expiresAt)}
            {left != null && left >= 0 && (
              <span className="text-xs text-muted-foreground">{t("ariregister.dns.daysLeft", { days: left })}</span>
            )}
            {left != null && left < 0 && <span className="text-xs">{t("ariregister.dns.expired")}</span>}
          </span>
        </Field>

        <Field label={t("ariregister.dns.registrar")}>
          {check.registrar ? (
            check.registrarUrl ? (
              <a className="text-primary hover:underline" href={check.registrarUrl} target="_blank" rel="noreferrer">
                {check.registrar}
              </a>
            ) : (
              check.registrar
            )
          ) : (
            "-"
          )}
        </Field>

        <Field label={t("ariregister.dns.registrant")}>{check.registrantName || "-"}</Field>
        <Field label={t("ariregister.dns.domain")}>
          <span className="font-mono text-xs">{check.domain}</span>
        </Field>
        <Field label={t("ariregister.dns.status")}>
          {check.domainStatus.length ? (
            <span className="flex flex-wrap gap-1">
              {check.domainStatus.map((s) => (
                <Badge key={s} variant="outline" className="font-normal">
                  {s}
                </Badge>
              ))}
            </span>
          ) : (
            "-"
          )}
        </Field>
      </dl>

      <div className="grid gap-3 sm:grid-cols-2">
        <RecordList label={t("ariregister.dns.nameservers")} values={check.nameservers} />
        <RecordList
          label={t("ariregister.dns.mx")}
          values={check.mxRecords.map((m) => `${m.priority}  ${m.exchange}`)}
          empty={t("ariregister.dns.noMx")}
        />
        <RecordList label={t("ariregister.dns.aRecords")} values={[...check.aRecords, ...check.aaaaRecords]} />
        <RecordList label={t("ariregister.dns.cname")} values={check.cname ? [check.cname] : []} />
      </div>

      <div className="space-y-2">
        <MailAuth label={t("ariregister.dns.spf")} value={check.spf} t={t} />
        <MailAuth label={t("ariregister.dns.dmarc")} value={check.dmarc} t={t} />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
        <span>
          {t(check.cached ? "ariregister.dns.cached" : "ariregister.dns.fresh", { date: fmtDateTime(check.checkedAt) })}
        </span>
        {check.error && (
          <span className="text-amber-600 dark:text-amber-400">
            {t("ariregister.dns.partial", { message: check.error })}
          </span>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("et-EE");
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" });
}

/** The headline answer: a known vendor name, or the bare record we could not map. */
function ProviderTile({
  icon: Icon,
  label,
  value,
  fallback,
}: {
  icon: typeof Server;
  label: string;
  value: string | null;
  fallback: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3" /> {label}
      </div>
      <div className={cn("mt-1 truncate text-sm font-semibold", !value && "font-normal text-muted-foreground")}>
        {value ?? fallback}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function RecordList({ label, values, empty = "-" }: { label: string; values: string[]; empty?: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {values.length === 0 ? (
        <div className="text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="space-y-0.5">
          {values.map((v) => (
            <li key={v} className="break-all font-mono text-xs">
              {v}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** SPF/DMARC row - present or missing is itself the interesting fact. */
function MailAuth({ label, value, t }: { label: string; value: string | null; t: T }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-xs">
      <span className="inline-flex items-center gap-1 font-medium text-muted-foreground">
        <ShieldCheck className={cn("size-3", value ? "text-green-600 dark:text-green-400" : "text-muted-foreground")} />
        {label}
      </span>
      {value ? (
        <code className="min-w-0 flex-1 break-all rounded bg-muted/50 px-1.5 py-0.5 font-mono">{value}</code>
      ) : (
        <span className="text-muted-foreground">{t("ariregister.dns.missing")}</span>
      )}
    </div>
  );
}
